import {
    createSearchRequestUrl,
    createSearchResultsUrl,
    isAbortError,
    isSearchShortcut,
    normalizeQuery,
} from './search-utils.mjs';

const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusableElements = (container) =>
    [...container.querySelectorAll(focusableSelector)].filter(
        (element) => !element.closest('[inert]') && element.getClientRects().length > 0,
    );

const setInert = (element, inert) => {
    element.inert = inert;

    if (inert) {
        element.setAttribute('inert', '');
    } else {
        element.removeAttribute('inert');
    }
};

const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);

    if (className) {
        element.className = className;
    }

    if (text !== undefined && text !== null) {
        element.textContent = String(text);
    }

    return element;
};

const toSafeUrl = (value, fallback = '#') => {
    try {
        const url = new URL(String(value || ''), window.location.origin);

        if (['http:', 'https:'].includes(url.protocol)) {
            return url.toString();
        }
    } catch {
        // The fallback keeps malformed API data inert.
    }

    return fallback;
};

const createSkeletonCard = () => {
    const card = createElement('article', 'SearchProductCard SearchProductCard--skeleton');
    card.setAttribute('aria-hidden', 'true');
    card.append(createElement('div', 'SearchProductCard__media'));

    const details = createElement('div', 'SearchProductCard__skeletonDetails');
    details.append(
        createElement('span', 'SearchProductCard__skeletonLine'),
        createElement('span', 'SearchProductCard__skeletonLine SearchProductCard__skeletonLine--title'),
        createElement('span', 'SearchProductCard__skeletonLine SearchProductCard__skeletonLine--price'),
    );
    card.append(details);

    return card;
};

const createProductCard = (item) => {
    const card = createElement('article', 'SearchProductCard');
    const link = createElement('a', 'SearchProductCard__link');
    link.href = toSafeUrl(item?.url);
    link.dataset.searchResultLink = '';
    link.setAttribute('aria-label', `Zobacz produkt: ${item?.full_name || item?.name || 'Produkt'}`);

    const media = createElement('div', 'SearchProductCard__media');
    const image = createElement('img', 'SearchProductCard__image');
    image.src = toSafeUrl(item?.image?.src, '');
    image.alt = String(item?.image?.alt || item?.name || '');
    image.loading = 'lazy';
    image.decoding = 'async';
    image.width = Number.parseInt(item?.image?.width, 10) || 640;
    image.height = Number.parseInt(item?.image?.height, 10) || 800;

    if (item?.image?.srcset) {
        image.srcset = String(item.image.srcset);
        image.sizes = String(item.image.sizes || '(max-width: 720px) 84px, (max-width: 1000px) 44vw, (max-width: 1408px) 22vw, 302px');
    }

    media.append(image);

    if (item?.badge && !item?.price?.on_sale) {
        media.append(createElement('span', 'SearchProductCard__badge', item.badge));
    }

    const details = createElement('div', 'SearchProductCard__details');
    details.append(createElement('p', 'SearchProductCard__category', item?.category || 'Produkt'));
    details.append(createElement('h3', 'SearchProductCard__title', item?.name || 'Produkt'));

    if (item?.subtitle) {
        details.append(createElement('p', 'SearchProductCard__subtitle', item.subtitle));
    }

    const price = createElement('p', 'SearchProductCard__price');
    price.textContent = String(item?.price?.current || item?.price?.display || 'Zobacz produkt');

    details.append(price);

    if (item?.available === false) {
        details.append(createElement('span', 'SearchProductCard__availability', 'Chwilowo niedostępny'));
    }

    link.append(media, details);
    card.append(link);

    return card;
};

const dispatchSearchEvent = (type, detail = {}) => {
    document.dispatchEvent(
        new CustomEvent('seluvia:search', {
            detail: { type, ...detail },
        }),
    );
};

export const initSiteSearch = (header, { beforeOpen } = {}) => {
    const overlay = header.querySelector('[data-site-search]');
    const toggle = header.querySelector('[data-search-toggle]');

    if (!overlay || !toggle || overlay.dataset.searchReady === 'true') {
        return null;
    }

    const form = overlay.querySelector('[data-search-form]');
    const shell = overlay.querySelector('.SiteSearch__shell');
    const input = overlay.querySelector('[data-search-input]');
    const clearButton = overlay.querySelector('[data-search-clear]');
    const closeButton = overlay.querySelector('[data-search-close]');
    const results = overlay.querySelector('[data-search-results]');
    const heading = overlay.querySelector('[data-search-heading]');
    const resultCount = overlay.querySelector('[data-search-count]');
    const viewAll = overlay.querySelector('[data-search-view-all]');
    const productGrid = overlay.querySelector('[data-search-product-grid]');
    const statePanel = overlay.querySelector('[data-search-state]');
    const liveRegion = overlay.querySelector('[data-search-live]');
    const popularButtons = [...overlay.querySelectorAll('[data-search-term]')];

    if (
        !form
        || !input
        || !clearButton
        || !results
        || !heading
        || !viewAll
        || !productGrid
        || !statePanel
    ) {
        return null;
    }

    const endpoint = overlay.dataset.endpoint || '';
    const minLength = Math.max(1, Number.parseInt(overlay.dataset.minLength, 10) || 2);
    const maxLength = Math.max(minLength, Number.parseInt(overlay.dataset.maxLength, 10) || 80);
    const resultsLimit = Math.max(1, Number.parseInt(overlay.dataset.resultsLimit, 10) || 4);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const compactViewport = window.matchMedia('(max-width: 720px)');
    const cache = new Map();
    let controller = null;
    let debounceTimer = 0;
    let closeTimer = 0;
    let focusTimer = 0;
    let requestSequence = 0;
    let returnFocus = null;

    const announce = (message) => {
        if (!liveRegion) {
            return;
        }

        liveRegion.textContent = '';
        window.setTimeout(() => {
            liveRegion.textContent = message;
        }, 20);
    };

    const syncInputControls = () => {
        const query = normalizeQuery(input.value, maxLength);
        clearButton.hidden = query.length === 0;
        overlay.classList.toggle('has-query', query.length > 0);

        return query;
    };

    const setHeading = (value) => {
        heading.textContent = value;
    };

    const setStatePanel = ({ eyebrow, title, body, actions = [] }) => {
        const eyebrowElement = createElement('p', 'SiteSearch__stateEyebrow', eyebrow);
        const titleElement = createElement('h3', '', title);
        const bodyElement = createElement('p', 'SiteSearch__stateText', body);
        statePanel.replaceChildren(eyebrowElement, titleElement, bodyElement);

        if (actions.length > 0) {
            const actionsElement = createElement('div', 'SiteSearch__stateActions');

            actions.forEach(({ label, value, retry }) => {
                const button = createElement('button', '', label);
                button.type = 'button';

                if (retry) {
                    button.dataset.searchRetry = '';
                } else {
                    button.dataset.searchTerm = value;
                }

                actionsElement.append(button);
            });

            statePanel.append(actionsElement);
        }
    };

    const setVisualState = (state) => {
        results.dataset.state = state;
        results.setAttribute('aria-busy', String(state === 'loading'));
        if (resultCount) {
            resultCount.hidden = state !== 'results';
        }
    };

    const renderIdle = () => {
        setVisualState('idle');
        setHeading('Szukaj produktów');
        viewAll.hidden = true;
        productGrid.hidden = true;
        statePanel.hidden = false;
        setStatePanel({
            eyebrow: 'Wyszukiwanie Seluvia',
            title: 'Czego szukasz?',
            body: 'Wpisz nazwę produktu, kategorię lub kolor.',
        });
    };

    const renderShortQuery = (query) => {
        setVisualState('short');
        setHeading('Jeszcze chwila…');
        viewAll.hidden = true;
        productGrid.hidden = true;
        statePanel.hidden = false;
        setStatePanel({
            eyebrow: 'Wyszukiwanie',
            title: `Wpisz jeszcze ${Math.max(1, minLength - [...query].length)} znak.`,
            body: `Do wyszukiwania potrzebujemy co najmniej ${minLength} znaków.`,
        });
    };

    const renderLoading = (query) => {
        setVisualState('loading');
        setHeading(`Szukamy dla „${query}”`);
        viewAll.hidden = true;
        statePanel.hidden = true;
        productGrid.hidden = false;
        productGrid.replaceChildren(...Array.from({ length: resultsLimit }, createSkeletonCard));
    };

    const renderResults = (payload, query) => {
        const items = Array.isArray(payload?.items) ? payload.items : [];

        if (items.length === 0) {
            setVisualState('empty');
            setHeading(`Brak wyników dla „${query}”`);
            viewAll.hidden = true;
            productGrid.hidden = true;
            statePanel.hidden = false;

            const suggestions = (Array.isArray(payload?.suggestions) ? payload.suggestions : [])
                .slice(0, 5)
                .map((value) => ({ label: String(value), value: String(value) }));
            setStatePanel({
                eyebrow: 'Brak wyników',
                title: `Nie znaleźliśmy „${query}”.`,
                body: 'Sprawdź pisownię, spróbuj szerszego hasła albo wybierz jedną z podpowiedzi.',
                actions: suggestions,
            });
            announce(`Brak produktów dla hasła ${query}.`);
            dispatchSearchEvent('empty', { query });
            return;
        }

        setVisualState('results');
        setHeading(`Dla hasła „${query}”`);
        statePanel.hidden = true;
        productGrid.hidden = false;
        productGrid.replaceChildren(...items.map(createProductCard));
        viewAll.href = toSafeUrl(
            payload?.view_all_url,
            createSearchResultsUrl(form.action, query, window.location.href),
        );
        viewAll.hidden = false;

        const total = Math.max(items.length, Number.parseInt(payload?.total, 10) || items.length);
        if (resultCount) {
            const noun = { one: 'produkt', few: 'produkty', many: 'produktów', other: 'produktów' }[
                new Intl.PluralRules('pl').select(total)
            ];
            resultCount.textContent = `${total} ${noun}${total > items.length ? ` · ${items.length} w podglądzie` : ''}`;
        }
        viewAll.setAttribute('aria-label', `Zobacz wszystkie wyniki dla „${query}” (${total})`);
        announce(`Znaleziono ${total} produktów dla hasła ${query}.`);
        dispatchSearchEvent('results', { query, total });
    };

    const renderError = (query) => {
        setVisualState('error');
        setHeading('Coś przerwało wyszukiwanie.');
        viewAll.hidden = true;
        productGrid.hidden = true;
        statePanel.hidden = false;
        setStatePanel({
            eyebrow: 'Chwilowa przerwa',
            title: 'Nie udało się pobrać wyników.',
            body: 'Sprawdź połączenie i spróbuj ponownie. Twoje hasło nadal tu jest.',
            actions: [{ label: 'Spróbuj ponownie', retry: true }],
        });
        announce('Nie udało się pobrać wyników. Możesz spróbować ponownie.');
        dispatchSearchEvent('error', { query });
    };

    const remember = (key, payload) => {
        if (cache.size >= 25) {
            cache.delete(cache.keys().next().value);
        }

        cache.set(key, payload);
    };

    const search = async (rawQuery, { force = false } = {}) => {
        const query = normalizeQuery(rawQuery, maxLength);

        window.clearTimeout(debounceTimer);
        controller?.abort();

        if (query.length === 0) {
            renderIdle();
            return;
        }

        if ([...query].length < minLength) {
            renderShortQuery(query);
            return;
        }

        const cacheKey = query.toLocaleLowerCase('pl-PL');

        if (!force && cache.has(cacheKey)) {
            renderResults(cache.get(cacheKey), query);
            return;
        }

        renderLoading(query);
        controller = new AbortController();
        const sequence = ++requestSequence;
        dispatchSearchEvent('request', { query });

        try {
            const response = await fetch(
                createSearchRequestUrl(endpoint, query, resultsLimit, window.location.href),
                {
                    method: 'GET',
                    headers: { Accept: 'application/json' },
                    credentials: 'same-origin',
                    signal: controller.signal,
                },
            );
            const payload = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(payload?.message || `HTTP ${response.status}`);
            }

            if (sequence !== requestSequence || query !== normalizeQuery(input.value, maxLength)) {
                return;
            }

            remember(cacheKey, payload);
            renderResults(payload, query);
        } catch (error) {
            if (isAbortError(error) || sequence !== requestSequence) {
                return;
            }

            renderError(query);
        }
    };

    const queueSearch = (query, immediate = false) => {
        window.clearTimeout(debounceTimer);
        controller?.abort();

        if (!query || [...query].length < minLength) {
            search(query);
            return;
        }

        renderLoading(query);
        debounceTimer = window.setTimeout(() => search(query), immediate ? 0 : 240);
    };

    const selectTerm = (value) => {
        const query = normalizeQuery(value, maxLength);
        input.value = query;
        syncInputControls();
        input.focus({ preventScroll: true });
        queueSearch(query, true);
        dispatchSearchEvent('suggestion', { query });
    };

    const isOpen = () => overlay.classList.contains('is-open');

    const open = () => {
        if (isOpen()) {
            input.focus({ preventScroll: true });
            return;
        }

        const requestedReturnFocus = document.activeElement;
        beforeOpen?.();
        window.clearTimeout(closeTimer);
        window.clearTimeout(focusTimer);
        returnFocus = requestedReturnFocus instanceof HTMLElement
            && !requestedReturnFocus.closest('[aria-hidden="true"], [inert]')
            ? requestedReturnFocus
            : toggle;
        setInert(overlay, false);
        overlay.setAttribute('aria-hidden', 'false');
        toggle.setAttribute('aria-expanded', 'true');
        header.classList.add('is-search-open');
        document.body.style.setProperty(
            '--seluvia-search-scrollbar-width',
            `${Math.max(0, window.innerWidth - document.documentElement.clientWidth)}px`,
        );
        document.body.classList.add('has-site-search-open');

        if (shell) {
            shell.scrollTop = 0;
        }

        if (compactViewport.matches) {
            // Keeping focus inside the original tap is required for the iOS keyboard to open reliably.
            overlay.classList.add('is-open');
            input.focus({ preventScroll: true });
        } else {
            window.requestAnimationFrame(() => overlay.classList.add('is-open'));
            focusTimer = window.setTimeout(
                () => input.focus({ preventScroll: true }),
                reducedMotion.matches ? 0 : 180,
            );
        }

        const query = syncInputControls();
        if (query) {
            queueSearch(query, true);
        }
        dispatchSearchEvent('open');
    };

    const close = ({ restoreFocus = true } = {}) => {
        if (!isOpen() && overlay.getAttribute('aria-hidden') === 'true') {
            return;
        }

        window.clearTimeout(debounceTimer);
        window.clearTimeout(focusTimer);
        controller?.abort();
        requestSequence += 1;
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
        toggle.setAttribute('aria-expanded', 'false');
        header.classList.remove('is-search-open');
        document.body.classList.remove('has-site-search-open');
        document.body.style.removeProperty('--seluvia-search-scrollbar-width');
        closeTimer = window.setTimeout(
            () => setInert(overlay, true),
            reducedMotion.matches ? 0 : 420,
        );

        if (restoreFocus && returnFocus instanceof HTMLElement) {
            returnFocus.focus({ preventScroll: true });
        }
        dispatchSearchEvent('close');
    };

    toggle.addEventListener('click', () => (isOpen() ? close() : open()));
    closeButton?.addEventListener('click', () => close());

    clearButton.addEventListener('click', () => {
        input.value = '';
        syncInputControls();
        renderIdle();
        input.focus({ preventScroll: true });
        controller?.abort();
        requestSequence += 1;
        dispatchSearchEvent('clear');
    });

    input.addEventListener('input', () => {
        const query = syncInputControls();
        queueSearch(query);
    });

    input.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' && results.dataset.state === 'results') {
            const firstResult = productGrid.querySelector('[data-search-result-link]');

            if (firstResult) {
                event.preventDefault();
                firstResult.focus();
            }
        }
    });

    form.addEventListener('submit', (event) => {
        const query = normalizeQuery(input.value, maxLength);

        if ([...query].length < minLength) {
            event.preventDefault();
            renderShortQuery(query);
            input.focus();
            return;
        }

        event.preventDefault();
        window.location.assign(createSearchResultsUrl(form.action, query, window.location.href));
    });

    popularButtons.forEach((button) => {
        button.addEventListener('click', () => selectTerm(button.dataset.searchTerm || button.textContent));
    });

    statePanel.addEventListener('click', (event) => {
        const retry = event.target.closest('[data-search-retry]');
        const suggestion = event.target.closest('[data-search-term]');

        if (retry) {
            search(input.value, { force: true });
        } else if (suggestion) {
            selectTerm(suggestion.dataset.searchTerm || suggestion.textContent);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (isSearchShortcut(event)) {
            event.preventDefault();
            open();
            return;
        }

        if (!isOpen()) {
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }

        if (event.key !== 'Tab') {
            return;
        }

        const focusable = getFocusableElements(overlay);
        const first = focusable[0];
        const last = focusable.at(-1);

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
        }
    });

    syncInputControls();
    setInert(overlay, true);
    overlay.dataset.searchReady = 'true';

    return { open, close, isOpen };
};
